import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} CoverageCalculatorRefs
 * @property {HTMLInputElement} areaInput
 * @property {HTMLInputElement[]} wasteOptions
 * @property {HTMLElement} result
 * @property {HTMLElement} quantityOutput
 * @property {HTMLElement} wasteSummary
 * @property {HTMLElement} subtotalOutput
 * @property {HTMLElement} availabilityNotice
 */

/**
 * Converts a project area into the whole-number product quantity submitted by
 * Horizon's native product form.
 *
 * @extends {Component<CoverageCalculatorRefs>}
 */
class CoverageCalculator extends Component {
  requiredRefs = [
    'areaInput',
    'wasteOptions',
    'result',
    'quantityOutput',
    'wasteSummary',
    'subtotalOutput',
    'availabilityNotice',
  ];

  connectedCallback() {
    super.connectedCallback();
    this.calculate();
  }

  calculate() {
    const area = Number.parseFloat(this.refs.areaInput.value);
    const selectedWaste = this.refs.wasteOptions.find((option) => option.checked);
    const wastePercent = Number.parseInt(selectedWaste?.value ?? '0', 10);

    if (!Number.isFinite(area) || area <= 0) {
      this.refs.result.hidden = true;
      this.#setMinimumQuantity();
      return;
    }

    const quantitySelector = this.#getQuantitySelector();
    const quantityValues = quantitySelector?.getCurrentValues?.();
    const minimum = quantityValues?.min ?? 1;
    const increment = quantityValues?.step ?? 1;
    const calculatedArea = area * (1 + wastePercent / 100);
    const quantity = Math.max(
      minimum,
      minimum + Math.ceil((calculatedArea - minimum - 1e-9) / increment) * increment
    );
    const effectiveMaximum = quantitySelector?.getEffectiveMax?.() ?? null;

    this.refs.quantityOutput.textContent = quantity.toLocaleString();
    this.refs.wasteSummary.textContent =
      wastePercent === 0 ? 'No extra material added' : `Includes ${wastePercent}% extra for cuts & waste`;
    this.refs.subtotalOutput.textContent = this.#formatSubtotal(quantity);
    this.refs.result.hidden = false;

    const exceedsAvailability = effectiveMaximum !== null && quantity > effectiveMaximum;
    this.refs.availabilityNotice.hidden = !exceedsAvailability;
    this.refs.availabilityNotice.textContent = exceedsAvailability
      ? `This estimate exceeds the ${effectiveMaximum.toLocaleString()} sq ft currently available. Please contact us for help.`
      : '';

    this.#setQuantity(quantity);
  }

  #getQuantitySelector() {
    return this.closest('product-form-component')?.querySelector('quantity-selector-component') ?? null;
  }

  #setMinimumQuantity() {
    const quantitySelector = this.#getQuantitySelector();
    const minimum = quantitySelector?.getCurrentValues?.().min ?? 1;
    this.#setQuantity(minimum);
  }

  /** @param {number} quantity */
  #setQuantity(quantity) {
    const quantitySelector = this.#getQuantitySelector();
    if (!quantitySelector) return;

    quantitySelector.setValue?.(quantity.toString());
    quantitySelector.updateButtonStates?.();
    quantitySelector.dispatchEvent(new QuantitySelectorUpdateEvent(quantity));
  }

  /** @param {number} quantity */
  #formatSubtotal(quantity) {
    const unitPrice = Number.parseInt(this.dataset.unitPrice ?? '0', 10);
    const moneyFormat = this.dataset.moneyFormat ?? '{{amount}}';
    const currency = this.dataset.currency ?? 'USD';

    return formatMoney(unitPrice * quantity, moneyFormat, currency);
  }
}

if (!customElements.get('coverage-calculator')) {
  customElements.define('coverage-calculator', CoverageCalculator);
}
